/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router'
import { useServiceContext } from '../../root'
import { useSearchService } from '../../hooks/useSearchService'
import PageFrame from '../../components/PageFrame'
import { SearchBar } from '../browser/SearchBar'
import { SearchTableFilter } from './SearchTableFilter'
import { SearchTable } from './SearchTable'
import { RcmTableDataWrapper } from '../../components/rcm-table/RcmTableDataWrapper'
import RcmTableFilterTrigger from '../../components/rcm-table/RcmTableFilterTrigger'
import { SEARCH_TYPE_V2 } from '../../constants/search'
import Events from '../../lib/Events'
import type { SearchResult } from './types'
import PageMenu from './PageMenu'

export default function SearchPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const {
    service: {
      filter: { translate }
    },
    api,
    bus
  } = useServiceContext()

  const { assembleFiltersFromFormikValues, getOffsetAndLimit } = useSearchService()
  const subscriptions = useRef<Array<() => void>>([])
  const refreshTableRef = useRef<(() => void) | null>(null)
  // Ref updated every render (before effects run), so loadData always reads the
  // current URL params even when called from a stale refreshTable closure.
  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams

  // Parse URL parameters
  const urlQuery = searchParams.get('query') || ''
  const urlType = searchParams.get('type') || 'filename'
  const urlShow = searchParams.get('show') || 'all'
  const urlFilterId = searchParams.get('filter')

  const [isSavedFilterLoading, setIsSavedFilterLoading] = useState(urlFilterId ? parseInt(urlFilterId) > 0 : false)
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [isShowFullTable, setIsShowFullTable] = useState(urlShow !== 'all')
  const [searchState, setSearchState] = useState({
    type: urlShow !== 'all' ? urlShow : urlType,
    query: urlQuery
  })
  const [searchResults, setSearchResults] = useState<SearchResult>({
    filename: [],
    content: [],
    docUID: [],
    filenameHits: 0,
    contentHits: 0,
    docUIDHits: 0,
    took: 0,
    timedOut: false,
    esRunning: false
  })

  // Subscribe to search bar filter updates
  useEffect(() => {
    const unsubscribe = bus.addEventListener(Events.UPDATE_SEARCH_BAR_FILTER, (event: any) => {
      const { payload } = event.detail || {}
      if (payload) {
        setSearchState({
          query: payload.phrase,
          type: payload.searchType
        })
      }
    })

    subscriptions.current.push(() => bus.removeEventListener(Events.UPDATE_SEARCH_BAR_FILTER, unsubscribe as any))

    return () => {
      subscriptions.current.forEach((unsub) => unsub())
    }
  }, [bus])

  // Trigger search when URL params change
  useEffect(() => {
    if (urlQuery) {
      // Map URL param to SEARCH_TYPE_V2 constant
      const typeFromUrl = urlShow !== 'all' ? urlShow : urlType
      let mappedType = typeFromUrl

      if (typeFromUrl === 'filename') {
        mappedType = SEARCH_TYPE_V2.filename
      } else if (typeFromUrl === 'content') {
        mappedType = SEARCH_TYPE_V2.content
      } else if (typeFromUrl === 'docUID') {
        mappedType = SEARCH_TYPE_V2.docUID
      } else if (typeFromUrl === 'all') {
        mappedType = SEARCH_TYPE_V2.all
      }

      setSearchState({
        query: urlQuery,
        type: mappedType
      })
      setIsShowFullTable(urlShow !== 'all')

      // Trigger table reload
      if (refreshTableRef.current) {
        refreshTableRef.current()
      }
    }
  }, [urlQuery, urlType, urlShow])

  // Load search data function for RcmTableDataWrapper
  const loadData = async (metaFilter: any, filter: any) => {
    setIsLoadingData(true)

    try {
      // Prefer filter parameter from table context, fallback to localStorage
      let filterValues: any = {}

      if (filter && Object.keys(filter).length > 0) {
        // Use filter passed from table context (e.g., from applyFilter)
        filterValues = filter
      } else {
        // Fallback: Load persisted filter values from localStorage
        const persistenceKey = 'searchPage_filter'
        let persistedFilters: any = {}
        try {
          const persisted = localStorage.getItem(persistenceKey)
          if (persisted) {
            persistedFilters = JSON.parse(persisted)
          }
        } catch (e) {
          console.error('Failed to load persisted filters:', e)
        }

        // Read from searchParamsRef (updated every render before effects fire) so
        // loadData always uses the current URL phrase, never a stale searchState closure.
        const _params = searchParamsRef.current
        const _show = _params.get('show') || 'all'
        const _query = _params.get('query') || ''
        const _type = _show !== 'all' ? _show : _params.get('type') || 'filename'
        filterValues = {
          phrase: _query,
          searchType: _type,
          lastUpdatedType: persistedFilters.lastUpdatedType || '',
          updatedFrom: persistedFilters.updatedFrom ? new Date(persistedFilters.updatedFrom) : undefined,
          updatedTo: persistedFilters.updatedTo ? new Date(persistedFilters.updatedTo) : undefined,
          state: persistedFilters.state || 'ALL',
          itemType: persistedFilters.itemType || 'ALL',
          docType: persistedFilters.docType
        }
      }

      // Assemble API parameters
      const apiParams = assembleFiltersFromFormikValues(filterValues, false)
      const _currentShow = searchParamsRef.current.get('show') || 'all'
      const _currentType = _currentShow !== 'all' ? _currentShow : searchParamsRef.current.get('type') || 'filename'
      const { limit, offset } = getOffsetAndLimit({ show: _currentShow, searchType: _currentType }, metaFilter)

      // Make API call
      const result = await api.files.search({
        ...apiParams,
        limit,
        offset
      })

      setSearchResults(result)

      // Return expected format for RcmTableDataWrapper
      return {
        elements: [],
        totalElements: result.filenameHits + result.contentHits + result.docUIDHits
      }
    } catch (error) {
      console.error('Search failed:', error)
      return {
        elements: [],
        totalElements: 0
      }
    } finally {
      setIsLoadingData(false)
    }
  }

  // Handle "Show More" click - navigate to full table view
  const handleShowMore = (searchType: string) => {
    setIsShowFullTable(true)

    // Update URL - the useEffect will handle state update and table refresh
    const params = new URLSearchParams(searchParams)
    params.set('show', searchType)
    navigate(`/search?${params.toString()}`)
  }

  // Handle "Back to Preview" click
  const handleBackToPreview = () => {
    setIsShowFullTable(false)

    // Update URL - the useEffect will handle state update and table refresh
    const params = new URLSearchParams(searchParams)
    params.set('show', 'all')
    navigate(`/search?${params.toString()}`)
  }

  // Get count object for translation
  const getCountObj = (count: number) => ({ count })

  return (
    <div id="browserPageSearch">
      <RcmTableDataWrapper
        loadData={loadData}
        defaultSortBy="UPDATED_ON"
        defaultSortDesc={true}
        persistenceKey="searchPage"
        reloadController={(refreshFn) => {
          refreshTableRef.current = refreshFn
        }}
      >
        <PageFrame
          header={
            <div className="mb10">
              <div className="d-flex flex-wrap">
                <div className="align-left d-flex">
                  <h1 id="search-page-title">{translate('SEARCH_TITLE')}</h1>
                  <SearchBar defaultType={searchState.type} defaultQuery={searchState.query} />
                </div>
                <div className="align-right align-bottom d-flex">
                  <PageMenu />
                  <RcmTableFilterTrigger />
                </div>
              </div>
            </div>
          }
          content={
            <div id="browser-wrapper">
              <SearchTableFilter
                isSavedFilterLoading={isSavedFilterLoading}
                setIsSavedFilterLoading={setIsSavedFilterLoading}
                filterId={urlFilterId || undefined}
                phrase={searchState.query}
                searchType={searchState.type}
              />

              <p className="searched-info">
                {translate('SEARCH_YOUR_SEARCH')}{' '}
                <strong className="whitespaceprewrap" title={searchState.query}>
                  {`"${searchState.query || '-'}"`}
                </strong>{' '}
                {translate('SEARCH_RESULTED_IN_THESE')}
              </p>

              {/* Content Section */}
              {(searchState.type === SEARCH_TYPE_V2.all || searchState.type === SEARCH_TYPE_V2.content) && (
                <div className="pb5 pt5">
                  <strong>
                    {translate('CONTENT')}
                    {isLoadingData ? (
                      <span> (...)</span>
                    ) : (
                      <span>
                        {' '}
                        (
                        {searchResults.contentHits === 1
                          ? translate('SEARCH_RESULTS_ONE', getCountObj(searchResults.contentHits))
                          : translate('SEARCH_RESULTS_MANY', getCountObj(searchResults.contentHits))}
                        )
                      </span>
                    )}
                  </strong>
                  <SearchTable
                    results={searchResults.content}
                    totalItems={searchResults.contentHits}
                    resultType={SEARCH_TYPE_V2.content}
                    showFullTable={isShowFullTable && searchState.type === SEARCH_TYPE_V2.content}
                    onShowMoreClick={() => handleShowMore(SEARCH_TYPE_V2.content)}
                    onBackClick={handleBackToPreview}
                    isLoading={isLoadingData}
                  />
                </div>
              )}

              {/* Filename Section */}
              {(searchState.type === SEARCH_TYPE_V2.all || searchState.type === SEARCH_TYPE_V2.filename) && (
                <div className="pb5 pt5">
                  <strong className="pb5">
                    {translate('NAME')}
                    {isLoadingData ? (
                      <span> (...)</span>
                    ) : (
                      <span>
                        {' '}
                        (
                        {searchResults.filenameHits === 1
                          ? translate('SEARCH_RESULTS_ONE', getCountObj(searchResults.filenameHits))
                          : translate('SEARCH_RESULTS_MANY', getCountObj(searchResults.filenameHits))}
                        )
                      </span>
                    )}
                  </strong>
                  <SearchTable
                    results={searchResults.filename}
                    totalItems={searchResults.filenameHits}
                    resultType={SEARCH_TYPE_V2.filename}
                    showFullTable={isShowFullTable && searchState.type === SEARCH_TYPE_V2.filename}
                    onShowMoreClick={() => handleShowMore(SEARCH_TYPE_V2.filename)}
                    onBackClick={handleBackToPreview}
                    isLoading={isLoadingData}
                  />
                </div>
              )}

              {/* DocUID Section */}
              {(searchState.type === SEARCH_TYPE_V2.all || searchState.type === SEARCH_TYPE_V2.docUID) && (
                <div className="pb5 pt5">
                  <strong className="pb5">
                    {translate('DOCUMENT_IDENTIFIER')}
                    {isLoadingData ? (
                      <span> (...)</span>
                    ) : (
                      <span>
                        {' '}
                        (
                        {searchResults.docUIDHits === 1
                          ? translate('SEARCH_RESULTS_ONE', getCountObj(searchResults.docUIDHits))
                          : translate('SEARCH_RESULTS_MANY', getCountObj(searchResults.docUIDHits))}
                        )
                      </span>
                    )}
                  </strong>
                  <SearchTable
                    results={searchResults.docUID}
                    totalItems={searchResults.docUIDHits}
                    resultType={SEARCH_TYPE_V2.docUID}
                    showFullTable={isShowFullTable && searchState.type === SEARCH_TYPE_V2.docUID}
                    onShowMoreClick={() => handleShowMore(SEARCH_TYPE_V2.docUID)}
                    onBackClick={handleBackToPreview}
                    isLoading={isLoadingData}
                  />
                </div>
              )}
            </div>
          }
        />
      </RcmTableDataWrapper>
    </div>
  )
}
